import { captureException, startSpan } from "@sentry/react";

import {
    INotificationResponse,
    INotificationsResponse,
    IUserChangeLogViewResponse,
} from "../../types";
import { doGet, doPostJson } from "../core/http";

export const getUnseenChangelogs =
    async (): Promise<IUserChangeLogViewResponse> => {
        const url = "/api/user-changelog-views/unseen";
        try {
            const response = await startSpan(
                { name: "getUnseenChangelogs" },
                async () => {
                    return doGet(url, true);
                }
            );
            if (response.error) {
                captureException(response.error);
                return { error: response.error };
            }
            return {
                unseenChangelogs: response.data,
            };
        } catch (e) {
            captureException(e);
            return { error: "Failed to get changelogs." };
        }
    };

export const markChangelogSeen = async (
    changelogId: string
): Promise<{ error?: string }> => {
    const url = `/api/user-changelog-views/${changelogId}/seen`;

    try {
        const { error } = await startSpan({ name: "markChangelogSeen" }, () => {
            return doPostJson(url, true, {});
        });

        if (error) {
            captureException(error);
            return { error: "Failed to mark changelog as seen." };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to mark changelog as seen." };
    }
};

export const getUnreadNotifications =
    async (): Promise<INotificationsResponse> => {
        const url = "/api/notifications";
        try {
            const response = await startSpan(
                { name: "getUnreadNotifications" },
                async () => {
                    return doGet(url, true);
                }
            );

            if (response.error) {
                captureException(response.error);
                return { error: response.error };
            }

            return { notifications: response.data };
        } catch (e) {
            captureException(e);
            return { error: "Failed to retrieve unread notifications." };
        }
    };

export const markNotificationAsRead = async (
    notificationId: string
): Promise<INotificationResponse> => {
    const url = `/api/notifications/${notificationId}/read`;
    try {
        const response = await startSpan(
            { name: "markNotificationAsRead" },
            async () => {
                return doPostJson(url, true, {});
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { notification: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to mark notification as read." };
    }
};

export const markAllNotificationsAsRead =
    async (): Promise<INotificationsResponse> => {
        const url = "/api/notifications/read/all";
        try {
            const response = await startSpan(
                { name: "markAllNotificationsAsRead" },
                async () => {
                    return doPostJson(url, true, {});
                }
            );

            if (response.error) {
                captureException(response.error);
                return { error: response.error };
            }

            return { notifications: response.data };
        } catch (e) {
            captureException(e);
            return { error: "Failed to mark all notifications as read." };
        }
    };
